'use client';

import { useEffect, useState } from 'react';
import { faAngleLeft, faAngleRight, faPlus } from '@fortawesome/pro-light-svg-icons';
import { DataTestIdEnum } from '@medprev-web-app/datatestid';

import { RescheduleDayColumn } from './reschedule-day-column.component';
import { RescheduleEmptyState } from './reschedule-empty-state.component';
import { RescheduleSlotsSkeleton } from './reschedule-slots-skeleton.component';
import { useRescheduleAppointment } from './_hooks/use-reschedule-appointment.hook';
import { useRescheduleSlots } from './_hooks/use-reschedule-slots.hook';

import type { RescheduleAvailableHour } from '@/app/(main)/(pages)/(auth)/agendamentos/_components/card-appointment/variants/_components/appointment-options/_services/get-appointment-avaiable-hours.service';
import { ScheduleType } from '@/app/(main)/(pages)/busca/[slug]/_types/search-partner-data.type';
import { Avatar } from '@/components/ui/avatar/avatar.component';
import { Icon } from '@/components/ui/icon.component';
import { ModalCloseButton } from '@/components/ui/modal-close-button/modal-close-button.component';
import { useProgressiveModal } from '@/components/ui/progressive-modal/progressive-modal.hook';
import {
  SecondaryTabProvider,
  SecondaryTabs,
  SecondaryTabsContent,
  SecondaryTabsList,
  SecondaryTabsTrigger,
} from '@/components/ui/secondary-tab.component';
import { SheetClose, SheetTitle } from '@/components/ui/sheet';
import { Typography } from '@/components/ui/typography/typography.component';
import { TypographyVariant } from '@/components/ui/typography/typography.type';
import { useMediaQuery } from '@/hooks/use-media-query';

interface RescheduleSlotsModalProps {
  appointmentId: string;
  professional: { name: string; avatarUrl?: string };
  isArrivalOrder?: boolean;
  onAppointmentUpdate?: (slot: RescheduleAvailableHour) => void;
}

const DEFAULT_VISIBLE_DAYS = 3;
const ARRIVAL_ORDER_MOBILE_VISIBLE_DAYS = 2;

const ARROW_BTN_WRAPPER = 'flex items-center justify-center px-1 py-3 transition-opacity disabled:cursor-not-allowed disabled:opacity-40';
const ARROW_CIRCLE_BASE = 'flex items-center justify-center rounded-full border size-6';
const ARROW_CIRCLE_ENABLED = 'border-gray-500 hover:bg-gray-100';
const ARROW_CIRCLE_DISABLED = 'border-gray-400';

export function RescheduleSlotsModal({
  appointmentId,
  professional,
  isArrivalOrder = false,
  onAppointmentUpdate,
}: RescheduleSlotsModalProps) {
  const { onClose } = useProgressiveModal();
  const isAtLeastSm = useMediaQuery('(min-width: 640px)');

  const [scheduleTypeFilter, setScheduleTypeFilter] = useState<ScheduleType | undefined>(undefined);

  const isArrivalOrderTab
    = scheduleTypeFilter === ScheduleType.ARRIVAL_ORDER || scheduleTypeFilter === ScheduleType.LABORATORIAL;
  const visibleDays
    = (isArrivalOrder || isArrivalOrderTab) && !isAtLeastSm ? ARRIVAL_ORDER_MOBILE_VISIBLE_DAYS : DEFAULT_VISIBLE_DAYS;

  const slots = useRescheduleSlots(appointmentId, visibleDays, scheduleTypeFilter);
  const { selectSlot, isReschedulingSlotId } = useRescheduleAppointment({
    appointmentId,
    onSuccess: (slot) => {
      onAppointmentUpdate?.(slot);
      onClose();
    },
  });

  const isHybrid = slots.hasScheduled && slots.hasArrivalOrder;
  const defaultTab = slots.hasScheduled ? ScheduleType.SCHEDULED : ScheduleType.ARRIVAL_ORDER;

  useEffect(() => {
    if (isHybrid && scheduleTypeFilter === undefined) {
      setScheduleTypeFilter(defaultTab);
    }
  }, [isHybrid, scheduleTypeFilter, defaultTab]);

  const renderSlots = () => (
    <div className="flex flex-col gap-5">
      <div className="flex w-full items-start justify-center gap-4">
        <button
          type="button"
          onClick={slots.goToPreviousDays}
          disabled={!slots.hasPrevious}
          data-testid={DataTestIdEnum.APPOINTMENT_RESCHEDULE_PREV_DAYS}
          className={ARROW_BTN_WRAPPER}
          aria-label="Dias anteriores"
        >
          <span className={`${ARROW_CIRCLE_BASE} ${slots.hasPrevious ? ARROW_CIRCLE_ENABLED : ARROW_CIRCLE_DISABLED}`}>
            <Icon icon={faAngleLeft} className="text-gray-700" />
          </span>
        </button>

        <div className="flex flex-1 items-start gap-4">
          {slots.availableDays.map((day) => (
            <RescheduleDayColumn
              key={day.date}
              day={day}
              isReschedulingSlotId={isReschedulingSlotId}
              onSlotClick={selectSlot}
            />
          ))}
        </div>

        <button
          type="button"
          onClick={slots.goToNextDays}
          disabled={!slots.hasNext}
          data-testid={DataTestIdEnum.APPOINTMENT_RESCHEDULE_NEXT_DAYS}
          className={ARROW_BTN_WRAPPER}
          aria-label="Próximos dias"
        >
          <span className={`${ARROW_CIRCLE_BASE} ${slots.hasNext ? ARROW_CIRCLE_ENABLED : ARROW_CIRCLE_DISABLED}`}>
            <Icon icon={faAngleRight} className="text-gray-700" />
          </span>
        </button>
      </div>

      {slots.hasMoreSlots && (
        <button
          type="button"
          onClick={slots.loadMoreSlots}
          data-testid={DataTestIdEnum.APPOINTMENT_RESCHEDULE_LOAD_MORE_SLOTS}
          className="mx-auto flex items-center gap-2 rounded-lg px-4 py-2 text-base font-semibold text-green-500 hover:bg-green-50"
        >
          <Icon icon={faPlus} />
          Ver outros horários
        </button>
      )}
    </div>
  );

  const renderContent = () => {
    if (slots.isLoading || slots.isLoadingHours) {
      return <RescheduleSlotsSkeleton visibleDays={visibleDays} isArrivalOrder={isArrivalOrder} />;
    }

    if (slots.isEmpty || (!slots.hasScheduled && !slots.hasArrivalOrder)) {
      return <RescheduleEmptyState />;
    }

    if (isHybrid && scheduleTypeFilter === undefined) {
      return <RescheduleSlotsSkeleton visibleDays={visibleDays} isArrivalOrder={isArrivalOrder} />;
    }

    if (!isHybrid) {
      if (slots.availableDays.length === 0) {
        return <RescheduleEmptyState />;
      }
      return renderSlots();
    }

    return (
      <SecondaryTabProvider>
        <SecondaryTabs
          defaultValue={defaultTab}
          value={scheduleTypeFilter}
          onValueChange={(value) => setScheduleTypeFilter(value as ScheduleType)}
          className="w-full flex flex-col gap-5"
        >
          <SecondaryTabsList
            data-testid={DataTestIdEnum.APPOINTMENT_RESCHEDULE_TABS_LIST}
            className="z-auto"
          >
            <SecondaryTabsTrigger
              value={ScheduleType.SCHEDULED}
              data-testid={DataTestIdEnum.APPOINTMENT_RESCHEDULE_TAB_SCHEDULED}
              className="text-sm leading-5 pb-1 px-2"
            >
              Hora marcada
            </SecondaryTabsTrigger>
            <SecondaryTabsTrigger
              value={ScheduleType.ARRIVAL_ORDER}
              data-testid={DataTestIdEnum.APPOINTMENT_RESCHEDULE_TAB_ARRIVAL_ORDER}
              className="text-sm leading-5 pb-1 px-2"
            >
              Ordem de chegada
            </SecondaryTabsTrigger>
          </SecondaryTabsList>

          <SecondaryTabsContent value={ScheduleType.SCHEDULED}>
            {slots.availableDays.length === 0 ? <RescheduleEmptyState /> : renderSlots()}
          </SecondaryTabsContent>

          <SecondaryTabsContent value={ScheduleType.ARRIVAL_ORDER}>
            {slots.availableDays.length === 0 ? <RescheduleEmptyState /> : renderSlots()}
          </SecondaryTabsContent>
        </SecondaryTabs>
      </SecondaryTabProvider>
    );
  };

  return (
    <div
      data-testid={DataTestIdEnum.APPOINTMENT_RESCHEDULE_MODAL}
      className="flex w-full flex-col gap-5"
    >
      <div className="flex items-center justify-between gap-4">
        <SheetTitle className="self-start text-2xl font-bold text-gray-900">
          Horários disponíveis
        </SheetTitle>

        <SheetClose
          asChild
          className="self-start"
          onClick={onClose}
        >
          <ModalCloseButton data-testid={DataTestIdEnum.APPOINTMENT_RESCHEDULE_CLOSE_BUTTON} />
        </SheetClose>
      </div>

      <div className="flex items-start gap-4">
        {professional.avatarUrl
          ? (
              <Avatar
                src={professional.avatarUrl}
                alt={professional.name}
                size="lg"
                width={64}
                height={64}
              />
            )
          : <div className="size-16 rounded-lg border border-gray-300 bg-gray-100" />}
        <Typography
          variant={TypographyVariant.MEDIUM_SUBTITLE}
          className="flex-1 self-stretch font-semibold !text-green-500"
          data-testid={DataTestIdEnum.APPOINTMENT_RESCHEDULE_PROFESSIONAL_NAME}
        >
          {professional.name}
        </Typography>
      </div>

      {renderContent()}
    </div>
  );
}
